import { useTrackEvent } from "libs/analytics";
import { VIDEO_EDITOR_EXPORT } from "../../constants/Events";
import CreativeEditorSDK from "@cesdk/cesdk-js";
import { CreativeBaseEditorModal } from "../photo-editor/creative-base-editor";
import { useState, useRef, useEffect } from "react";
import {
  saveScene,
  loadScene,
  SceneMetadata,
} from "../../utils/cesdk-scene-storage";
import { handleCESDKUpload } from "../../utils/cesdk-upload-handler";
import {
  createFilteredUploadAssetSource,
  addUploadedAsset,
  initializeUploadedAssets,
  setAssetSourceChangeNotifier,
} from "../../utils/cesdk-upload-asset-source";

/**
 * Calculate recommended video bitrate based on resolution.
 * Higher resolutions need higher bitrates for quality preservation.
 */
function getRecommendedBitrate(width: number, height: number): number {
  const resolution = width * height;
  if (resolution <= 640 * 480) return 2_500_000; // 480p: 2.5 Mbps
  if (resolution <= 1280 * 720) return 5_000_000; // 720p: 5 Mbps
  if (resolution <= 1920 * 1080) return 8_000_000; // 1080p: 8 Mbps
  return 10_000_000; // 4K and above: 10 Mbps
}

export function CreativeVideoEditorModal(props: {
  show: boolean;
  video: string;
  assetId: string;
  assetUrl: string;
  isReplacingOriginal: boolean;
  onClose(): void;
  onSave(blob: Blob): void;
  progress: number | null;
  saveButtonText?: string;
  message?: string;
  isSaving?: boolean;
}) {
  const trackEvent = useTrackEvent();
  const [hasChanges, setHasChanges] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (props.show) {
      setHasChanges(false);
      setIsExporting(false);
      // Note: Asset initialization happens in initializeUploadedAssetSources() during onInit
      // to ensure proper sequencing with asset source registration
    }
  }, [props.show]);

  // Cleanup on unmount or when modal closes
  useEffect(() => {
    if (!props.show && unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
      // Don't clear uploaded assets on close - they persist in collection
      // Only clear when explicitly needed (e.g., when saving a new version)
    }
  }, [props.show]);

  if (!props.show) {
    return null;
  }

  return (
    <CreativeBaseEditorModal
      style={
        {
          "--ubq-InspectorBar-background": "var(--ubq-canvas)",
        } as React.CSSProperties
      }
      title="Video Editor"
      onClose={props.onClose}
      hasChanges={hasChanges}
      progress={
        props.progress
          ? {
              label: "Saving...",
              percentage: props.progress,
            }
          : undefined
      }
      saveButtonText={props.saveButtonText}
      message={props.message}
      isSaving={props.isSaving || isExporting}
      saveDisabled={!hasChanges}
      onUploadHandler={async (
        file: File,
        onProgress: (progress: number) => void,
        context?: { sourceId?: string; group?: string }
      ): Promise<{ id: string; [key: string]: unknown }> => {
        // Handle upload and add to asset source
        const assetDefinition = await handleCESDKUpload(
          file,
          onProgress,
          context
        );
        addUploadedAsset(assetDefinition);
        return assetDefinition as { id: string; [key: string]: unknown };
      }}
      onSave={async (instance: CreativeEditorSDK) => {
        // Immediately show loading state
        setIsExporting(true);

        try {
          const engine = instance.engine;
          let videoBlob: Blob;

          if (hasChanges) {
            const page = engine.scene.getCurrentPage();
            if (!page) {
              throw new Error("No current page found");
            }

            try {
              const progressCallback = () => {
                // Progress tracking available here if needed
              };

              // Get page dimensions to preserve original video aspect ratio and resolution
              const pageWidth = engine.block.getWidth(page);
              const pageHeight = engine.block.getHeight(page);

              videoBlob = await engine.block.exportVideo(
                page,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                "video/mp4" as any,
                progressCallback,
                {
                  targetWidth: Math.round(pageWidth),
                  targetHeight: Math.round(pageHeight),
                  videoBitrate: getRecommendedBitrate(pageWidth, pageHeight),
                  audioBitrate: 128_000, // 128 kbps
                  framerate: 30,
                  h264Profile: 77, // Main profile
                  h264Level: 52, // Level 5.2 (supports up to 4K)
                }
              );
            } catch {
              // Create archive to preserve changes
              await engine.scene.saveToArchive();

              // Return original video as fallback
              const response = await fetch(props.video);
              videoBlob = await response.blob();
            }

            // Save scene string if replacing original asset
            if (props.isReplacingOriginal) {
              try {
                // Save scene string - ceSDK will handle URL conversion internally
                const sceneString = await engine.scene.saveToString();
                const metadata: SceneMetadata = {
                  assetId: props.assetId,
                  assetType: "video",
                  sourceUrl: props.assetUrl,
                  updatedAt: Date.now(),
                };
                await saveScene(props.assetId, sceneString, metadata);
              } catch (error) {
                console.error(
                  "VideoEditor onSave - Failed to save scene string:",
                  error
                );
              }
            }
          } else {
            // No changes made, return original video
            const response = await fetch(props.video);
            videoBlob = await response.blob();
          }

          trackEvent({ eventName: VIDEO_EDITOR_EXPORT });
          props.onSave(videoBlob);
        } catch (error) {
          console.error("VideoEditor onSave - Export failed:", error);
          throw error;
        } finally {
          setIsExporting(false);
        }
      }}
      onInit={async (instance) => {
        const cleanup = await initVideoEditorUIConfig(
          instance,
          props.video,
          props.assetId,
          props.assetUrl,
          setHasChanges
        );
        unsubscribeRef.current = cleanup;
      }}
    />
  );
}

async function initializeUploadedAssetSources(
  instance: CreativeEditorSDK
): Promise<void> {
  try {
    await initializeUploadedAssets();
  } catch (error) {
    console.error(
      "Failed to initialize uploaded assets in initVideoEditorUIConfig:",
      error
    );
    // Continue with empty asset list - editor can still work
  }

  // Set up notifier to refresh UI when assets are deleted
  setAssetSourceChangeNotifier(() => {
    try {
      const assetApi = instance.engine.asset;
      assetApi.assetSourceContentsChanged("sked-uploads-video");
      assetApi.assetSourceContentsChanged("sked-uploads-image");
      assetApi.assetSourceContentsChanged("sked-uploads-audio");
    } catch (error) {
      console.error("Failed to notify asset source changed:", error);
    }
  });

  // Register filtered asset sources for categorization
  // IMPORTANT: Sources must be registered BEFORE addDefaultAssetSources/addDemoAssetSources
  // to ensure they're available when the UI initializes
  const imagesAssetSource = createFilteredUploadAssetSource("image");
  const videosAssetSource = createFilteredUploadAssetSource("video");
  const audioAssetSource = createFilteredUploadAssetSource("audio");

  // Register all sources with the engine BEFORE adding default/demo sources
  // This ensures our custom sources are available when the UI initializes
  try {
    instance.engine.asset.addSource(
      imagesAssetSource as Parameters<typeof instance.engine.asset.addSource>[0]
    );
    instance.engine.asset.addSource(
      videosAssetSource as Parameters<typeof instance.engine.asset.addSource>[0]
    );
    instance.engine.asset.addSource(
      audioAssetSource as Parameters<typeof instance.engine.asset.addSource>[0]
    );
  } catch (error) {
    console.error("Error adding asset sources:", error);
    throw error; // Re-throw to prevent continuing if source registration fails
  }

  // Replace built-in asset library entries with our custom sources only
  // This removes stock assets and shows only uploaded assets in built-in tabs
  try {
    const builtInEntries = [
      { id: "ly.img.image", customSourceId: imagesAssetSource.id },
      { id: "ly.img.video", customSourceId: videosAssetSource.id },
      { id: "ly.img.audio", customSourceId: audioAssetSource.id },
    ];

    for (const entry of builtInEntries) {
      try {
        instance.ui.addAssetLibraryEntry({
          id: entry.id,
          sourceIds: [entry.customSourceId],
          previewLength: 3,
          gridColumns: 3,
          gridItemHeight: "auto",
          previewBackgroundType: "contain",
          gridBackgroundType: "cover",
          canRemove: true, // Enable delete button (ellipsis menu) on each asset
        });
      } catch (error) {
        console.warn(`Could not replace built-in entry ${entry.id}:`, error);
      }
    }
  } catch (error) {
    console.warn("Error replacing built-in asset library entries:", error);
  }
}

async function loadSavedSceneIfEnabled(
  instance: CreativeEditorSDK,
  videoUri: string,
  assetId: string,
  assetUrl: string,
  setHasChanges: (hasChanges: boolean) => void
): Promise<void> {
  try {
    const savedScene = await loadScene(assetId);

    if (
      savedScene &&
      savedScene.metadata.sourceUrl === assetUrl &&
      savedScene.metadata.assetType === "video"
    ) {
      await instance.engine.scene.loadFromString(savedScene.scene);
      setHasChanges(true);
    } else {
      await instance.engine.scene.createFromVideo(videoUri);
    }
  } catch (error) {
    console.error("VideoEditor - Failed to load scene, creating new:", error);
    await instance.engine.scene.createFromVideo(videoUri);
  }
}

export async function initVideoEditorUIConfig(
  instance: CreativeEditorSDK,
  videoUri: string,
  assetId: string,
  assetUrl: string,
  setHasChanges: (hasChanges: boolean) => void
) {
  // Initialize uploaded assets from collection FIRST, before adding any sources
  // This ensures assets are loaded and ready when the source is created
  await initializeUploadedAssetSources(instance);

  // Now add default and demo asset sources
  await instance.addDefaultAssetSources();

  // Add demo asset sources for video editing
  await instance.addDemoAssetSources({
    sceneMode: "Video",
  });

  // Handle the broken video in CESDK's built-in upload sources
  // Strategy: Replace the ly.img.upload asset library entry to use our custom sources
  // while keeping upload functionality via the onUpload callback
  // Update the ly.img.upload entry to show our custom sources alongside built-in upload
  // The upload button works via onUpload callback, our sources show clean uploaded assets
  try {
    instance.ui.updateAssetLibraryEntry("ly.img.upload", {
      sourceIds: [
        "sked-uploads-video",
        "sked-uploads-image",
        "sked-uploads-audio",
      ],
      canAdd: true,
      canRemove: true, // Enable ellipsis icon with delete option
      title: ({ sourceId }) => {
        if (sourceId === "sked-uploads-video") return "Videos";
        if (sourceId === "sked-uploads-image") return "Images";
        if (sourceId === "sked-uploads-audio") return "Audio";
        return "Uploads";
      },
    });
  } catch (error) {
    console.warn("Could not update ly.img.upload entry:", error);
  }

  // Disable placeholder and preview features
  instance.feature.enable("ly.img.placeholder", false);
  instance.feature.enable("ly.img.preview", false);

  // Try to load saved scene, fallback to creating new scene from video
  await loadSavedSceneIfEnabled(
    instance,
    videoUri,
    assetId,
    assetUrl,
    setHasChanges
  );

  // Get the current page to subscribe to changes
  const page = instance.engine.scene.getCurrentPage();
  if (!page) {
    return () => {};
  }

  // Add a delay to ignore initial loading events
  let isInitializing = true;
  setTimeout(() => {
    isInitializing = false;
  }, 2000); // 2 second delay to ignore loading events

  // Subscribe to block events to detect changes
  const unsubscribe = instance.engine.event.subscribe([page], (events) => {
    // Ignore events during initial loading
    if (isInitializing) {
      return;
    }

    const hasUpdates = events.some((event) => event.type === "Updated");
    const hasCreations = events.some((event) => event.type === "Created");
    const hasDeletions = events.some((event) => event.type === "Destroyed");

    if (hasUpdates || hasCreations || hasDeletions) {
      setHasChanges(true);
    }
  });

  return () => {
    unsubscribe();
  };
}
